import React, {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import "./App.css";

// 定义消息类型
interface Message {
  role: "user" | "assistant" | "system";
  content: string;
}

// 定义API响应类型
interface StreamResponseData {
  choices: Array<{
    delta: {
      content?: string;
    };
  }>;
}

const App: React.FC = () => {
  const [workersUrl, setWorkersUrl] = useState<string>(
    "https://my-first-worker.yeqiuchan00.workers.dev"
  );
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "您好！我是 DeepSeek 智能助手，请问有什么可以帮您的吗？",
    },
  ]);
  const [inputMessage, setInputMessage] = useState<string>("");
  const [isWaitingForResponse, setIsWaitingForResponse] =
    useState<boolean>(false);
  const [conversationHistory, setConversationHistory] = useState<Message[]>([
    {
      role: "system",
      content: "你是一个有帮助的AI助手",
    },
  ]);

  const chatMessagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 加载保存的 Workers URL
    const savedUrl = localStorage.getItem("deepseek-workers-url");
    if (savedUrl) {
      setWorkersUrl(savedUrl);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const saveWorkersUrl = (url: string): void => {
    localStorage.setItem("deepseek-workers-url", url);
  };

  const handleWorkersUrlChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const url = e.target.value;
    setWorkersUrl(url);
    saveWorkersUrl(url);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setInputMessage(e.target.value);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter" && !isWaitingForResponse) {
      handleSendMessage();
    }
  };

  const handleSendMessage = async (): Promise<void> => {
    const message = inputMessage.trim();
    if (!message || isWaitingForResponse || !workersUrl) {
      if (!workersUrl) {
        alert("请输入 Workers 服务地址");
      }
      return;
    }

    // 添加用户消息
    const userMessage: Message = { role: "user", content: message };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");

    // 更新对话历史
    const updatedHistory = [...conversationHistory, userMessage];
    setConversationHistory(updatedHistory);

    setIsWaitingForResponse(true);

    try {
      await sendStreamRequest(updatedHistory);
    } catch (error) {
      console.error("Workers 请求错误:", error);
      const errorMessage = error instanceof Error ? error.message : "未知错误";
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "抱歉，发生了错误：" + errorMessage,
        },
      ]);
    } finally {
      setIsWaitingForResponse(false);
    }
  };

  const sendStreamRequest = async (history: Message[]): Promise<void> => {
    if (!workersUrl) {
      throw new Error("Workers URL 未设置");
    }

    const response = await fetch(workersUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        messages: history,
        stream: true,
      }),
    });

    if (!response.ok) {
      throw new Error(
        `Workers 请求失败: ${response.status} ${response.statusText}`
      );
    }

    if (!response.body) {
      throw new Error("响应体为空");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let assistantMessage = "";

    // 先添加一个空的助手消息
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ") && !line.includes("[DONE]")) {
            try {
              const data: StreamResponseData = JSON.parse(line.slice(6));
              const content = data.choices[0]?.delta?.content || "";
              if (content) {
                assistantMessage += content;
                // 更新最后一条消息的内容
                setMessages((prev) => {
                  const newMessages = [...prev];
                  newMessages[newMessages.length - 1] = {
                    role: "assistant",
                    content: assistantMessage,
                  };
                  return newMessages;
                });
              }
            } catch (e) {
              // 忽略解析错误
              console.warn("解析流数据时出错:", e);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    // 更新对话历史
    setConversationHistory((prev) => [
      ...prev,
      {
        role: "assistant",
        content: assistantMessage,
      },
    ]);
  };

  const scrollToBottom = (): void => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  return (
    <div className="app">
      <div className="chat-container">
        <div className="chat-header">
          <h1>DeepSeek 智能助手</h1>
          <p>通过 Workers 代理访问 DeepSeek API</p>
        </div>

        <div className="workers-url-container">
          <input
            type="text"
            id="workersUrlInput"
            placeholder="请输入您的 Workers 服务地址 (例如: https://your-worker.your-subdomain.workers.dev)"
            value={workersUrl}
            onChange={handleWorkersUrlChange}
          />
        </div>

        <div className="chat-messages" ref={chatMessagesRef}>
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.role}-message`}>
              <div className="message-content">{message.content}</div>
            </div>
          ))}
          {isWaitingForResponse && (
            <div className="message assistant-message typing-indicator">
              思考中
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
        </div>

        <div className="chat-input-container">
          <div className="chat-input-wrapper">
            <input
              type="text"
              id="messageInput"
              placeholder="输入您的问题..."
              value={inputMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={isWaitingForResponse}
            />
            <button
              id="sendButton"
              onClick={handleSendMessage}
              disabled={isWaitingForResponse}
            >
              发送
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
